import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query'
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Select,
  Spinner,
  Table,
  Text,
  TextField
} from '@radix-ui/themes'
import { Cross2Icon } from '@radix-ui/react-icons'
import cn from 'classnames'
import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { z } from 'zod'
import {
  DATA_COVERAGE_ALERT_WINDOW_DAYS,
  DATA_COVERAGE_LIST_WINDOWS_DAYS,
  dataCoverageFilterSchema,
  dataCoverageListWindowSchema,
  measurementTypeSchema,
  type DataCoverageFilter,
  type DataCoverageListWindowDays,
  type MeasurementType
} from '@willie/schemas'
import { useTRPC, type TRPCInputs, type TRPCOutputs } from '~/utils/trpc'
import { StationTypeIcon } from '~/features/stations/components/StationTypeIcon'
import {
  COVERAGE_FILTER_LABELS,
  DATA_COVERAGE_LABEL,
  coverageColor,
  coverageColumnLabel,
  formatCoverageRatio
} from '~/features/stations/data-coverage/format'
import { OrgLink } from '~/features/organizations/components/OrgLink'

type Station = TRPCOutputs['ops']['stations']['list']['stations'][number]

type StationsSearch = TRPCInputs['ops']['stations']['list']

interface NamedEntity<TId extends string = string> {
  id: TId
  name: string
}

interface FilterChip {
  key: string
  label: string
  onRemove: () => void
}

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' })

// Names that collate equal (case, accents, punctuation) would interleave two orgs' rows; the id
// tiebreak keeps each group contiguous.
const compareNamedById = (a: NamedEntity, b: NamedEntity): number =>
  collator.compare(a.name, b.name) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)

const compareStations = (a: Station, b: Station): number =>
  compareNamedById(a.org, b.org) || compareNamedById(a.site, b.site) || compareNamedById(a, b)

// Without this the row's own onClick fires too and the cell link navigates twice.
const stopRowNavigation = (event: MouseEvent) => event.stopPropagation()

const formatCount = (value: number): string => value.toLocaleString('en-GB')

const CELL_LINK_CLASS = 'text-[var(--accent-11)] hover:underline'

const MEASUREMENT_TYPE_LABELS: Record<MeasurementType, string> = {
  water_volume: 'Water volume',
  water_level: 'Water level',
  rainfall: 'Rainfall'
}

const MEASUREMENT_TYPE_OPTIONS: NamedEntity<MeasurementType>[] = measurementTypeSchema.options.map((id) => ({
  id,
  name: MEASUREMENT_TYPE_LABELS[id]
}))

// Healthiest first; the schema lists the presets worst first.
const COVERAGE_OPTIONS: NamedEntity<DataCoverageFilter>[] = [...dataCoverageFilterSchema.options]
  .reverse()
  .map((id) => ({
    id,
    name: COVERAGE_FILTER_LABELS[id]
  }))

// Radix rejects an empty Select value, so "no filter" needs a sentinel; no uuid can collide with it.
const NO_FILTER = 'all'

const SEARCH_DEBOUNCE_MS = 300

const DIRECTORY_STALE_MS = 1000 * 60 * 5

const uuidSchema = z.string().uuid()

const parseSearchParam = <T,>(schema: z.ZodType<T>, value: unknown): T | undefined => {
  const parsed = schema.safeParse(value)
  return parsed.success ? parsed.data : undefined
}

const nameById = (options: NamedEntity[], id: string): string =>
  options.find((option) => option.id === id)?.name ?? id

// A selection the options don't contain renders a blank trigger while the filter is live.
const withUnknownSelection = <TId extends string>(
  options: NamedEntity<TId>[],
  selectedId: TId | undefined
): NamedEntity<TId>[] =>
  !selectedId || options.some((option) => option.id === selectedId)
    ? options
    : [...options, { id: selectedId, name: selectedId }]

interface DebouncedSearchField {
  value: string
  onChange: (next: string) => void
  reset: () => void
}

const useDebouncedSearchField = (
  urlValue: string,
  commit: (next: string | undefined) => void
): DebouncedSearchField => {
  const [value, setValue] = useState(urlValue)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const committed = useRef(urlValue)

  const cancel = () => clearTimeout(timer.current)

  useEffect(() => cancel, [])

  // Our own commit comes back around as a urlValue change; re-syncing on it would overwrite
  // keystrokes typed while that navigation was in flight.
  useEffect(() => {
    if (urlValue === committed.current) return
    cancel()
    committed.current = urlValue
    setValue(urlValue)
  }, [urlValue])

  const onChange = (next: string) => {
    setValue(next)
    cancel()
    timer.current = setTimeout(() => {
      const trimmed = next.trim()
      committed.current = trimmed
      commit(trimmed || undefined)
    }, SEARCH_DEBOUNCE_MS)
  }

  const reset = () => {
    cancel()
    committed.current = ''
    setValue('')
  }

  return { value, onChange, reset }
}

interface FilterSelectProps<TId extends string> {
  label: string
  allLabel: string
  value: TId | undefined
  options: NamedEntity<TId>[]
  disabled?: boolean
  onChange: (next: TId | undefined) => void
}

const FilterSelect = <TId extends string>({
  label,
  allLabel,
  value,
  options,
  disabled,
  onChange
}: FilterSelectProps<TId>) => {
  const items = withUnknownSelection(options, value)

  return (
    <Flex direction="column" gap="1" width="220px">
      <Text as="label" size="1" color="gray">
        {label}
      </Text>
      <Select.Root
        disabled={disabled}
        value={value ?? NO_FILTER}
        onValueChange={(next) => onChange(items.find((item) => item.id === next)?.id)}
      >
        <Select.Trigger className="w-full" />
        <Select.Content>
          <Select.Item value={NO_FILTER}>{allLabel}</Select.Item>
          {items.map((item) => (
            <Select.Item key={item.id} value={item.id}>
              {item.name}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
    </Flex>
  )
}

interface CoverageColumnHeaderProps {
  value: DataCoverageListWindowDays
  onChange: (next: DataCoverageListWindowDays) => void
}

const CoverageColumnHeader = ({ value, onChange }: CoverageColumnHeaderProps) => (
  <Flex align="center" gap="2">
    {DATA_COVERAGE_LABEL}
    <Select.Root
      size="1"
      value={String(value)}
      onValueChange={(next) => {
        const days = parseSearchParam(dataCoverageListWindowSchema, Number(next))
        if (days) onChange(days)
      }}
    >
      <Select.Trigger variant="soft" color="gray" aria-label={`${DATA_COVERAGE_LABEL} window`} />
      <Select.Content>
        {DATA_COVERAGE_LIST_WINDOWS_DAYS.map((days) => (
          <Select.Item key={days} value={String(days)}>
            {days} d
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>
  </Flex>
)

interface StationsFiltersProps {
  search: StationsSearch
  orgs: NamedEntity[]
  sites: NamedEntity[]
  searchField: DebouncedSearchField
  onChange: (patch: StationsSearch) => void
}

// The default window stays out of the URL, like every other unset filter.
const toCoverageWindowParam = (days: DataCoverageListWindowDays): DataCoverageListWindowDays | undefined =>
  days === DATA_COVERAGE_ALERT_WINDOW_DAYS ? undefined : days

const StationsFilters = ({ search, orgs, sites, searchField, onChange }: StationsFiltersProps) => (
  <Flex gap="3" wrap="wrap" align="end">
    <Flex direction="column" gap="1" flexGrow="1" minWidth="220px">
      <Text as="label" size="1" color="gray">
        Search
      </Text>
      <TextField.Root
        placeholder="Name, internal ID, station UUID…"
        value={searchField.value}
        onChange={(event) => searchField.onChange(event.target.value)}
      />
    </Flex>

    <FilterSelect
      label="Organization"
      allLabel="All organizations"
      value={search.orgId}
      options={orgs}
      onChange={(orgId) => onChange({ orgId, siteId: undefined })}
    />

    <FilterSelect
      label="Site"
      allLabel="All sites"
      value={search.siteId}
      options={sites}
      disabled={!search.orgId}
      onChange={(siteId) => onChange({ siteId })}
    />

    <FilterSelect
      label="Measurement type"
      allLabel="All measurement types"
      value={search.measurementType}
      options={MEASUREMENT_TYPE_OPTIONS}
      onChange={(measurementType) => onChange({ measurementType })}
    />

    <FilterSelect
      label={DATA_COVERAGE_LABEL}
      allLabel="Any coverage"
      value={search.coverage}
      options={COVERAGE_OPTIONS}
      onChange={(coverage) => onChange({ coverage })}
    />
  </Flex>
)

interface ActiveFilterChipsProps {
  chips: FilterChip[]
  onClearAll: () => void
}

const ActiveFilterChips = ({ chips, onClearAll }: ActiveFilterChipsProps) => {
  if (chips.length === 0) return null

  return (
    <Flex gap="2" wrap="wrap" align="center">
      {chips.map((chip) => (
        <Badge key={chip.key} color="gray" variant="soft" size="2">
          {chip.label}
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            aria-label={`Remove filter ${chip.label}`}
            onClick={chip.onRemove}
          >
            <Cross2Icon />
          </IconButton>
        </Badge>
      ))}
      <Button size="1" variant="ghost" color="gray" onClick={onClearAll}>
        Clear all
      </Button>
    </Flex>
  )
}

interface StationsCountProps {
  shown: number
  total: number
  cap: number
}

const StationsCount = ({ shown, total, cap }: StationsCountProps) => {
  if (total <= cap) {
    return (
      <Text size="1" color="gray">
        {formatCount(total)} station{total === 1 ? '' : 's'}
      </Text>
    )
  }

  return (
    <Text size="1" color="amber">
      Showing {formatCount(shown)} of {formatCount(total)} stations — narrow your filters for complete results
    </Text>
  )
}

const STATIONS_TABLE_COLUMNS = 6

interface StationsTableMessageProps {
  error: string | null
  hasActiveFilters: boolean
  onClearFilters: () => void
}

const StationsTableMessage = ({ error, hasActiveFilters, onClearFilters }: StationsTableMessageProps) => {
  if (error) return <Text color="red">{error}</Text>
  if (!hasActiveFilters) return <Text color="gray">No stations found.</Text>

  return (
    <Flex direction="column" align="start" gap="2">
      <Text color="gray">No stations match these filters</Text>
      <Button size="1" variant="soft" color="gray" onClick={onClearFilters}>
        Clear all
      </Button>
    </Flex>
  )
}

interface StationsTableProps extends StationsTableMessageProps {
  stations: Station[]
  coverageWindow: DataCoverageListWindowDays
  onCoverageWindowChange: (next: DataCoverageListWindowDays) => void
  onRowClick: (stationId: string) => void
}

// The header always renders: it holds the coverage window, which must stay reachable when nothing matches.
const StationsTable = ({
  stations,
  coverageWindow,
  onCoverageWindowChange,
  error,
  hasActiveFilters,
  onClearFilters,
  onRowClick
}: StationsTableProps) => (
  <Table.Root variant="surface">
    <Table.Header>
      <Table.Row>
        <Table.ColumnHeaderCell width="40px">
          <span className="sr-only">Type</span>
        </Table.ColumnHeaderCell>
        <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
        <Table.ColumnHeaderCell>Org</Table.ColumnHeaderCell>
        <Table.ColumnHeaderCell>Site</Table.ColumnHeaderCell>
        <Table.ColumnHeaderCell>Internal ID</Table.ColumnHeaderCell>
        <Table.ColumnHeaderCell>
          <CoverageColumnHeader value={coverageWindow} onChange={onCoverageWindowChange} />
        </Table.ColumnHeaderCell>
      </Table.Row>
    </Table.Header>
    <Table.Body>
      {error || stations.length === 0 ? (
        <Table.Row>
          <Table.Cell colSpan={STATIONS_TABLE_COLUMNS}>
            <Box py="5">
              <StationsTableMessage
                error={error}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={onClearFilters}
              />
            </Box>
          </Table.Cell>
        </Table.Row>
      ) : (
        stations.map((station) => (
          <Table.Row
            key={station.id}
            onClick={() => onRowClick(station.id)}
            className="cursor-pointer hover:bg-[var(--gray-a2)]"
          >
            <Table.Cell>
              <StationTypeIcon measurementType={station.measurement_type} stationType={station.station_type} />
            </Table.Cell>
            <Table.Cell>
              <Link
                to="/stations/$stationId"
                params={{ stationId: station.id }}
                onClick={stopRowNavigation}
                className={CELL_LINK_CLASS}
              >
                <Text size="2">{station.name}</Text>
              </Link>
            </Table.Cell>
            <Table.Cell>
              <OrgLink
                orgId={station.org.id}
                name={station.org.name}
                size="2"
                className={CELL_LINK_CLASS}
                onClick={stopRowNavigation}
              />
            </Table.Cell>
            <Table.Cell>
              <Link
                to="/stations"
                search={(prev) => ({
                  orgId: station.org.id,
                  siteId: station.site.id,
                  coverageWindow: prev.coverageWindow
                })}
                onClick={stopRowNavigation}
                className={CELL_LINK_CLASS}
              >
                <Text size="2">{station.site.name}</Text>
              </Link>
            </Table.Cell>
            <Table.Cell>
              {station.internal_id ? (
                <Text size="2" className="font-mono">
                  {station.internal_id}
                </Text>
              ) : (
                <Text size="1" color="gray">
                  —
                </Text>
              )}
            </Table.Cell>
            <Table.Cell>
              <Text size="2" color={coverageColor(station.coverage_ratio)}>
                {formatCoverageRatio(station.coverage_ratio)}
              </Text>
            </Table.Cell>
          </Table.Row>
        ))
      )}
    </Table.Body>
  </Table.Root>
)

const StationsListPage = () => {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const search = Route.useSearch()

  const setSearch = (patch: StationsSearch): void => {
    navigate({ to: '/stations', search: (prev: StationsSearch) => ({ ...prev, ...patch }), replace: true })
  }

  const searchField = useDebouncedSearchField(search.q ?? '', (q) => setSearch({ q }))

  const clearSearch = () => {
    searchField.reset()
    setSearch({ q: undefined })
  }

  const clearAll = () => {
    searchField.reset()
    navigate({ to: '/stations', search: { coverageWindow: search.coverageWindow }, replace: true })
  }

  const { data, isLoading, isPlaceholderData, error } = useQuery(
    trpc.ops.stations.list.queryOptions(search, { placeholderData: keepPreviousData })
  )
  const orgsQuery = useQuery(trpc.ops.orgs.list.queryOptions(undefined, { staleTime: DIRECTORY_STALE_MS }))
  const sitesQuery = useQuery(
    trpc.ops.sites.list.queryOptions(search.orgId ? { orgId: search.orgId } : skipToken, {
      staleTime: DIRECTORY_STALE_MS
    })
  )

  const orgs = orgsQuery.data ?? []
  const sites = sitesQuery.data ?? []

  const coverageWindow = search.coverageWindow ?? DATA_COVERAGE_ALERT_WINDOW_DAYS
  // A coverage filter comes back worst first, which the directory order would bury.
  const stations = useMemo(
    () => (search.coverage ? (data?.stations ?? []) : [...(data?.stations ?? [])].sort(compareStations)),
    [data, search.coverage]
  )

  const chips: FilterChip[] = [
    search.q ? { key: 'q', label: `Search: ${search.q}`, onRemove: clearSearch } : null,
    search.orgId
      ? {
          key: 'orgId',
          label: `Org: ${nameById(orgs, search.orgId)}`,
          onRemove: () => setSearch({ orgId: undefined, siteId: undefined })
        }
      : null,
    search.siteId
      ? {
          key: 'siteId',
          label: `Site: ${nameById(sites, search.siteId)}`,
          onRemove: () => setSearch({ siteId: undefined })
        }
      : null,
    search.measurementType
      ? {
          key: 'measurementType',
          label: `Type: ${MEASUREMENT_TYPE_LABELS[search.measurementType]}`,
          onRemove: () => setSearch({ measurementType: undefined })
        }
      : null,
    search.coverage
      ? {
          key: 'coverage',
          label: `${coverageColumnLabel(coverageWindow)}: ${COVERAGE_FILTER_LABELS[search.coverage]}`,
          onRemove: () => setSearch({ coverage: undefined })
        }
      : null
  ].filter((chip) => chip !== null)

  return (
    <Flex direction="column" gap="4">
      <Box>
        <Heading size="6">Stations</Heading>
        <Text size="2" color="gray">
          Fleet directory — health and leak detection coming next
        </Text>
      </Box>

      <StationsFilters search={search} orgs={orgs} sites={sites} searchField={searchField} onChange={setSearch} />

      <ActiveFilterChips chips={chips} onClearAll={clearAll} />

      {isLoading ? (
        <Flex align="center" gap="2">
          <Spinner size="2" />
          <Text color="gray">Loading…</Text>
        </Flex>
      ) : (
        <Flex direction="column" gap="4">
          <Flex align="center" gap="2">
            {data && !error && <StationsCount shown={stations.length} total={data.total} cap={data.cap} />}
            {isPlaceholderData && <Spinner size="1" />}
          </Flex>
          {/* Rows lag the chips by one fetch while the previous result is kept on screen. */}
          <Box className={cn(isPlaceholderData && 'opacity-60')}>
            <StationsTable
              stations={stations}
              coverageWindow={coverageWindow}
              onCoverageWindowChange={(days) => setSearch({ coverageWindow: toCoverageWindowParam(days) })}
              error={error?.message ?? null}
              hasActiveFilters={chips.length > 0}
              onClearFilters={clearAll}
              onRowClick={(stationId) => navigate({ to: '/stations/$stationId', params: { stationId } })}
            />
          </Box>
        </Flex>
      )}
    </Flex>
  )
}

export const Route = createFileRoute('/stations/')({
  component: StationsListPage,
  validateSearch: (raw: Record<string, unknown>): StationsSearch => {
    const q = typeof raw.q === 'string' ? raw.q.trim() : ''
    return {
      orgId: parseSearchParam(uuidSchema, raw.orgId),
      siteId: parseSearchParam(uuidSchema, raw.siteId),
      measurementType: parseSearchParam(measurementTypeSchema, raw.measurementType),
      coverage: parseSearchParam(dataCoverageFilterSchema, raw.coverage),
      coverageWindow: parseSearchParam(dataCoverageListWindowSchema, raw.coverageWindow),
      q: q || undefined
    }
  }
})
